// Chemin: C:\gestion-copro\src\pages\coproprietaire\MesSignalementsPage.tsx
// Liste des signalements du copropriétaire connecté
// Session 56 : création initiale

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  PlusCircle,
  Clock,
  CheckCircle,
  Send,
  XCircle,
  Eye,
  Filter,
  Loader2,
  Search,
  Calendar,
  ChevronRight,
  Image,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Signalement {
  id: string;
  titre: string;
  description: string;
  localisation: string;
  localisationDetail: string;
  statut: string;
  urgence: string;
  photos: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  flagPotentielCollectif?: boolean;
  flagMalfaconGPA?: boolean;
}

// Configuration des statuts
const STATUTS_CONFIG: Record<string, { 
  label: string; 
  bg: string; 
  text: string; 
  icon: React.ElementType;
  description: string;
}> = {
  nouveau: { 
    label: 'Nouveau', 
    bg: 'bg-blue-100', 
    text: 'text-blue-800',
    icon: AlertTriangle,
    description: 'Votre signalement a été reçu'
  },
  en_cours: { 
    label: 'En cours', 
    bg: 'bg-orange-100', 
    text: 'text-orange-800',
    icon: Clock,
    description: 'Le CS examine votre signalement'
  },
  transmis: { 
    label: 'Transmis', 
    bg: 'bg-purple-100', 
    text: 'text-purple-800',
    icon: Send,
    description: 'Transmis au syndic/prestataire'
  },
  resolu: { 
    label: 'Résolu', 
    bg: 'bg-green-100', 
    text: 'text-green-800',
    icon: CheckCircle,
    description: 'Le problème a été résolu'
  },
  clos: { 
    label: 'Clos', 
    bg: 'bg-gray-100', 
    text: 'text-gray-800',
    icon: CheckCircle,
    description: 'Dossier clôturé'
  },
  refuse: { 
    label: 'Hors périmètre', 
    bg: 'bg-red-100', 
    text: 'text-red-800',
    icon: XCircle,
    description: 'Ne relève pas du CS'
  }
};

const MesSignalementsPage: React.FC = () => {
  const location = useLocation();
  const { firebaseUser } = useAuth();
  const { copropriete } = useCopropriete();

  const [signalements, setSignalements] = useState<Signalement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState<string>('tous');
  const [recherche, setRecherche] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Afficher message de succès si redirection depuis création
  useEffect(() => {
    if (location.state?.success) {
      setShowSuccessMessage(true);
      setSuccessMessage(location.state.message || 'Signalement envoyé avec succès');
      // Nettoyer le state
      window.history.replaceState({}, document.title);
      // Masquer après 5 secondes
      setTimeout(() => setShowSuccessMessage(false), 5000);
    }
  }, [location.state]);

  // Charger les signalements
  useEffect(() => {
    const fetchSignalements = async () => {
      if (!firebaseUser?.uid || !copropriete?.id) {
        setLoading(false);
        return;
      }

      try {
        const signalementsRef = collection(db, 'signalements');
        const q = query(
          signalementsRef,
          where('coproprieteId', '==', copropriete.id),
          where('userId', '==', firebaseUser.uid),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const data: Signalement[] = [];

        snapshot.forEach((doc) => {
          data.push({
            id: doc.id,
            ...doc.data()
          } as Signalement);
        });

        setSignalements(data);
      } catch (error) {
        console.error('Erreur chargement signalements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSignalements();
  }, [firebaseUser?.uid, copropriete?.id]);

  // Filtrage
  const signalementsFiltres = signalements.filter(s => {
    // Filtre par statut
    if (filtreStatut !== 'tous' && s.statut !== filtreStatut) {
      return false;
    }
    // Filtre par recherche
    if (recherche) {
      const searchLower = recherche.toLowerCase();
      return (
        s.titre.toLowerCase().includes(searchLower) ||
        s.description.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: signalements.length,
    enCours: signalements.filter(s => ['nouveau', 'en_cours', 'transmis'].includes(s.statut)).length,
    resolus: signalements.filter(s => ['resolu', 'clos'].includes(s.statut)).length
  };

  // Formater la date
  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Formater la date relative
  const formatDateRelative = (timestamp: Timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    return formatDate(timestamp);
  };

  // Rendu badge statut
  const renderStatutBadge = (statut: string) => {
    const config = STATUTS_CONFIG[statut] || STATUTS_CONFIG.nouveau;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Message de succès */}
      {showSuccessMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800">{successMessage}</p>
          <button
            onClick={() => setShowSuccessMessage(false)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes signalements</h1>
          <p className="text-gray-600 mt-1">
            Suivez l'avancement de vos signalements
          </p>
        </div>
        <Link
          to="/espace-copro/signaler"
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <PlusCircle className="w-4 h-4" />
          Nouveau signalement
        </Link>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-2xl font-bold text-orange-600">{stats.enCours}</p>
          <p className="text-sm text-gray-500">En cours</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-2xl font-bold text-green-600">{stats.resolus}</p>
          <p className="text-sm text-gray-500">Résolus</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Recherche */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher dans mes signalements..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Filtre statut */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filtreStatut}
              onChange={(e) => setFiltreStatut(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="tous">Tous les statuts</option>
              <option value="nouveau">Nouveau</option>
              <option value="en_cours">En cours</option>
              <option value="transmis">Transmis</option>
              <option value="resolu">Résolu</option>
              <option value="clos">Clos</option>
              <option value="refuse">Hors périmètre</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des signalements */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : signalementsFiltres.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-gray-400" />
          </div>
          {signalements.length === 0 ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun signalement
              </h3>
              <p className="text-gray-500 mb-6">
                Vous n'avez pas encore fait de signalement
              </p>
              <Link
                to="/espace-copro/signaler"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <PlusCircle className="w-4 h-4" />
                Faire un signalement
              </Link>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun résultat
              </h3>
              <p className="text-gray-500">
                Aucun signalement ne correspond à vos critères de recherche
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {signalementsFiltres.map((signalement) => {
            const statutConfig = STATUTS_CONFIG[signalement.statut] || STATUTS_CONFIG.nouveau;
            
            return (
              <Link
                key={signalement.id}
                to={`/espace-copro/mes-signalements/${signalement.id}`}
                className="block bg-white rounded-xl border border-gray-200 p-4 hover:border-green-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  {/* Vignette photo ou icône */}
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {signalement.photos && signalement.photos.length > 0 ? (
                      <img
                        src={signalement.photos[0]}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-gray-400" />
                    )}
                  </div>

                  {/* Contenu principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {signalement.titre}
                      </h3>
                      {renderStatutBadge(signalement.statut)}
                    </div>

                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {signalement.description}
                    </p>

                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDateRelative(signalement.createdAt)}
                      </span>
                      <span>{signalement.localisationDetail}</span>
                      {signalement.photos && signalement.photos.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Image className="w-3.5 h-3.5" />
                          {signalement.photos.length}
                        </span>
                      )}
                    </div>

                    {/* Indicateurs spéciaux */}
                    {(signalement.flagPotentielCollectif || signalement.flagMalfaconGPA) && (
                      <div className="flex gap-2 mt-2">
                        {signalement.flagPotentielCollectif && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                            Potentiel collectif
                          </span>
                        )}
                        {signalement.flagMalfaconGPA && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700">
                            GPA potentielle
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Chevron */}
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>

                {/* Explication statut */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    {statutConfig.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Légende des statuts */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Légende des statuts</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(STATUTS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <div key={key} className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                  <Icon className="w-3 h-3" />
                  {config.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MesSignalementsPage;
